import * as THREE from "three";
import { isSolid } from "../engine/blocks";
import { moveWithCollisions } from "../engine/physics";
import type { World } from "../engine/world";
import { hash2 } from "../engine/noise";
import { Item } from "./items";
import type { Player } from "./player";

export interface Mob {
  kind: "slime" | "critter";
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  hp: number;
  yaw: number;
  cooldown: number;
  hop: number;
  mesh: THREE.Group;
}

export class MobWorld {
  mobs: Mob[] = [];
  readonly group = new THREE.Group();
  private acc = 0;

  update(dt: number, world: World, player: Player, onDrop: (x: number, y: number, z: number, id: number) => void): void {
    this.acc += dt;
    if (this.acc > 2.5) {
      this.acc = 0;
      this.maybeSpawn(world, player);
    }
    for (let i = this.mobs.length - 1; i >= 0; i--) {
      const m = this.mobs[i]!;
      this.tickMob(m, dt, world, player, onDrop);
      const dx = m.x - player.x;
      const dz = m.z - player.z;
      if (dx * dx + dz * dz > 90 * 90 || m.y < -4) {
        this.removeAt(i);
      }
    }
  }

  pick(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): Mob | null {
    const len = Math.hypot(dx, dy, dz) || 1;
    dx /= len;
    dy /= len;
    dz /= len;
    let best: Mob | null = null;
    let bestD = maxDist;
    for (const m of this.mobs) {
      const mx = m.x - ox;
      const my = m.y + 0.4 - oy;
      const mz = m.z - oz;
      const t = mx * dx + my * dy + mz * dz;
      if (t < 0 || t > bestD) continue;
      const px = ox + dx * t;
      const py = oy + dy * t;
      const pz = oz + dz * t;
      const dist = Math.hypot(m.x - px, m.y + 0.4 - py, m.z - pz);
      if (dist < 0.7) {
        best = m;
        bestD = t;
      }
    }
    return best;
  }

  strike(m: Mob, dmg: number, fromX: number, fromZ: number): void {
    m.hp -= dmg;
    const away = Math.atan2(m.x - fromX, m.z - fromZ);
    m.vx += Math.sin(away) * 6;
    m.vz += Math.cos(away) * 6;
    m.vy = 5;
  }

  hitAt(x: number, y: number, z: number, dmg: number): boolean {
    for (const m of this.mobs) {
      if (Math.abs(m.x - x) < 0.8 && Math.abs(m.z - z) < 0.8 && Math.abs(m.y + 0.4 - y) < 1.2) {
        m.hp -= dmg;
        const away = Math.atan2(m.x - x, m.z - z);
        m.vx += Math.sin(away) * 6;
        m.vz += Math.cos(away) * 6;
        m.vy = 5;
        return true;
      }
    }
    return false;
  }

  private maybeSpawn(world: World, player: Player): void {
    if (this.mobs.length >= 10) return;
    const ang = Math.random() * Math.PI * 2;
    const r = 18 + Math.random() * 22;
    const x = Math.floor(player.x + Math.cos(ang) * r);
    const z = Math.floor(player.z + Math.sin(ang) * r);
    let y = 90;
    for (let yy = 120; yy > 2; yy--) {
      if (isSolid(world.getBlock(x, yy, z))) {
        y = yy + 1;
        break;
      }
    }
    if (y < 4 || world.isWater(x, y, z)) return;
    const kind: Mob["kind"] = hash2(x, z, world.seed) > 0.55 ? "slime" : "critter";
    this.mobs.push(this.make(kind, x + 0.5, y, z + 0.5));
  }

  private make(kind: Mob["kind"], x: number, y: number, z: number): Mob {
    const g = new THREE.Group();
    if (kind === "slime") {
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.72, 0.62, 0.72),
        new THREE.MeshLambertMaterial({ color: 0x6bc84a }),
      );
      const eyeL = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.12, 0.08), new THREE.MeshLambertMaterial({ color: 0x1a1814 }));
      const eyeR = eyeL.clone();
      eyeL.position.set(-0.16, 0.1, 0.34);
      eyeR.position.set(0.16, 0.1, 0.34);
      g.add(body, eyeL, eyeR);
    } else {
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.62, 0.4, 0.4),
        new THREE.MeshLambertMaterial({ color: 0xe8a0b0 }),
      );
      body.position.y = 0.22;
      const head = new THREE.Mesh(
        new THREE.BoxGeometry(0.28, 0.26, 0.28),
        new THREE.MeshLambertMaterial({ color: 0xf0b8c0 }),
      );
      head.position.set(0.28, 0.28, 0);
      const leg = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.18, 0.12),
        new THREE.MeshLambertMaterial({ color: 0xd88898 }),
      );
      const l1 = leg.clone();
      const l2 = leg.clone();
      const l3 = leg.clone();
      const l4 = leg.clone();
      l1.position.set(0.18, 0.08, 0.14);
      l2.position.set(0.18, 0.08, -0.14);
      l3.position.set(-0.18, 0.08, 0.14);
      l4.position.set(-0.18, 0.08, -0.14);
      g.add(body, head, l1, l2, l3, l4);
    }
    g.position.set(x, y, z);
    this.group.add(g);
    return {
      kind,
      x,
      y,
      z,
      vx: 0,
      vy: 0,
      vz: 0,
      hp: kind === "slime" ? 8 : 5,
      yaw: 0,
      cooldown: 0,
      hop: 0,
      mesh: g,
    };
  }

  private tickMob(
    m: Mob,
    dt: number,
    world: World,
    player: Player,
    onDrop: (x: number, y: number, z: number, id: number) => void,
  ): void {
    m.hop -= dt;
    m.cooldown -= dt;
    const dx = player.x - m.x;
    const dz = player.z - m.z;
    const dist = Math.hypot(dx, dz);
    if (m.kind === "slime" && dist < 16 && m.hop <= 0) {
      const inv = 1 / (dist || 1);
      m.vx = dx * inv * 4.2;
      m.vz = dz * inv * 4.2;
      m.vy = 7;
      m.hop = 1.4;
    } else if (m.kind === "critter" && m.hop <= 0) {
      const a = Math.random() * Math.PI * 2;
      m.vx = Math.sin(a) * 2.2;
      m.vz = Math.cos(a) * 2.2;
      m.vy = 4;
      m.hop = 1.8 + Math.random();
      if (dist < 5) {
        m.vx = -dx / (dist || 1) * 5;
        m.vz = -dz / (dist || 1) * 5;
      }
    }
    m.vy -= 24 * dt;
    const moved = moveWithCollisions(world, m.x, m.y, m.z, m.vx * dt, m.vy * dt, m.vz * dt);
    m.x = moved.x;
    m.y = moved.y;
    m.z = moved.z;
    if (moved.hitY) m.vy = 0;
    if (moved.grounded) {
      m.vx *= 0.85;
      m.vz *= 0.85;
    }
    m.yaw = Math.atan2(m.vx, m.vz);
    m.mesh.position.set(m.x, m.y, m.z);
    m.mesh.rotation.y = m.yaw;

    if (m.kind === "slime" && dist < 0.9 && m.cooldown <= 0 && !player.flying) {
      player.hurt(2);
      m.cooldown = 1.1;
    }
    if (m.hp <= 0) {
      onDrop(m.x, m.y + 0.2, m.z, m.kind === "slime" ? Item.SLIME_GEL : Item.RAW_PORK);
      if (m.kind === "critter") onDrop(m.x, m.y + 0.2, m.z, Item.LEATHER);
      const idx = this.mobs.indexOf(m);
      if (idx >= 0) this.removeAt(idx);
    }
  }

  private removeAt(i: number): void {
    const m = this.mobs[i];
    if (!m) return;
    this.group.remove(m.mesh);
    this.mobs.splice(i, 1);
  }
}
